import { useMemo, useState } from "react"
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import ControlPointIcon from "@mui/icons-material/ControlPoint"
import EditOutlined from "@mui/icons-material/EditOutlined"
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined"
import { ColumnDef } from "@tanstack/react-table"
import { toast } from "react-toastify"

import { ChipStyled } from "src/shared/components"
import { DataGridMui } from "src/shared/container/data-grid-mui"
import {
  IntegrationProductsDocument,
  useIntegrationProductsQuery,
  useRemoveIntegrationProductMutation
} from "src/graphql/quark/quark.type"
import AddProductModal from "./AddProductModal"
import BulkMapProductsDialog from "./BulkMapProductsDialog"
import ProductSettingsDialog from "./ProductSettingsDialog"

type ProductRow = {
  productId: string
  name: string
  barcode?: string | null
  active: boolean
  externalSku?: string | null
  stockBuffer?: number | null
  maximumSalesQuantity?: number | null
}

const ProductsTab = ({
  connectionId,
  integrationName,
  defaultStockBuffer,
  canEdit
}: {
  connectionId: string
  integrationName: string
  defaultStockBuffer: number | null
  canEdit: boolean
}) => {
  const [addOpen, setAddOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [editing, setEditing] = useState<ProductRow | null>(null)

  const { data, loading } = useIntegrationProductsQuery({
    variables: { connectionId },
    fetchPolicy: "network-only"
  })

  const [removeProduct] = useRemoveIntegrationProductMutation({
    refetchQueries: [{ query: IntegrationProductsDocument, variables: { connectionId } }]
  })

  const handleRemove = async (row: ProductRow) => {
    try {
      await removeProduct({
        variables: { input: { connectionId, oneObjectId: row.productId } }
      })
      toast.success(`${row.name} removed`)
    } catch (error: any) {
      toast.error(error?.message ?? "Could not remove the product")
    }
  }

  const columns: ColumnDef<ProductRow, any>[] = useMemo(
    () => [
      { header: "Product", accessorKey: "name" },
      {
        header: "Barcode",
        accessorKey: "barcode",
        cell: ({ row }) => row.original.barcode ?? "—"
      },
      { header: "SKU on this integration", accessorKey: "externalSku" },
      {
        header: "Status",
        accessorKey: "active",
        cell: ({ row }) => (
          <ChipStyled
            size="small"
            variant="outlined"
            label={row.original.active ? "Active" : "Inactive"}
            color={row.original.active ? "success" : "default"}
          />
        )
      },
      {
        header: "Stock buffer",
        accessorKey: "stockBuffer",
        cell: ({ row }) =>
          row.original.stockBuffer != null ? (
            row.original.stockBuffer
          ) : (
            <Typography variant="body2" color="text.secondary">
              {defaultStockBuffer != null ? `Default (${defaultStockBuffer})` : "None"}
            </Typography>
          )
      },
      {
        header: "Max per order",
        accessorKey: "maximumSalesQuantity",
        cell: ({ row }) =>
          row.original.maximumSalesQuantity != null ? (
            row.original.maximumSalesQuantity
          ) : (
            <Typography variant="body2" color="text.secondary">
              No limit
            </Typography>
          )
      },
      {
        header: "",
        id: "actions",
        cell: ({ row }) => (
          <Box display="flex">
            <Tooltip title="Edit stock buffer and max per order">
              <span>
                <IconButton size="small" disabled={!canEdit} onClick={() => setEditing(row.original)}>
                  <EditOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remove">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!canEdit}
                  onClick={() => void handleRemove(row.original)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )
      }
    ],
    [canEdit, defaultStockBuffer]
  )

  const rows: ProductRow[] = data?.fusionIntegrationProducts ?? []

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
        <Button
          variant="outlined"
          startIcon={<UploadFileOutlined />}
          disabled={!canEdit}
          onClick={() => setBulkOpen(true)}
        >
          Bulk upload
        </Button>
        <Button
          variant="contained"
          startIcon={<ControlPointIcon />}
          disabled={!canEdit}
          onClick={() => setAddOpen(true)}
        >
          Add product
        </Button>
      </Box>

      <DataGridMui
        id="integration-products"
        data={rows}
        columns={columns}
        isLoading={loading}
        paginationMode="client"
        noDataComponent={<Box p={3}>No products are published to this integration yet.</Box>}
      />

      <AddProductModal
        connectionId={connectionId}
        integrationName={integrationName}
        defaultStockBuffer={defaultStockBuffer}
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />

      <BulkMapProductsDialog
        connectionId={connectionId}
        integrationName={integrationName}
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
      />

      <ProductSettingsDialog
        connectionId={connectionId}
        integrationName={integrationName}
        product={editing}
        defaultStockBuffer={defaultStockBuffer}
        onClose={() => setEditing(null)}
      />
    </Box>
  )
}

export default ProductsTab
